import React, { useEffect, useState } from "react";
import TextField from "../../../../../Components/common/TextField/TextField";
import ChangeFields from "./ChangeFields";
import AddUser from "./AddUser";
import { useDispatch, useSelector } from "react-redux";
import {
  createUserRequest,
  getAllUsersRequest,
  updateUserRequest,
  deleteUserRequest,
  getAdminRoleRequest,
  setUserChanged,
  publicKeyCheckRequest,
  publicKeyRegRequest,
  apiLimitterGetRequest,
  apiLimitterSetRequest,
} from "../../../../../store/actions/usersActions";
import Backdrop from "../../../../../Components/UI/Backdrop/Backdrop";
import useResizeWindow from "../../../../../utils/hook/useResizeWindows";
import EditIcon from "../../../../../Components/Icon-SVG/EditIcon";
import TrashBucketIcon from "../../../../../Components/Icon-SVG/TrashBucketIcon";
import Warning from "../../../../../Components/common/Warning/Warning";
import { roleConstants, supRoleConstants, allRoleConstants } from "../../../../../utils/constants";
import { evaluatePassword } from "../../../../../utils/passwordPolicy";

import "./styles.scss";
import APIIcon from "../../../../../Components/Icon-SVG/APIIcon";

const UserAccounts = ({ collapsed }) => {
  const dispatch = useDispatch();
  const width = useResizeWindow();
  const users = useSelector((state) => state?.users.users);
  const findAdminRole = useSelector((state) => state.users.role);
  const userChanged = useSelector((state) => state.users.userChanged);
  const apiLimiter = useSelector((state) => state?.users.apiLimiter);
  const userPublicKey = useSelector((state) => state?.users.publicKey);
  const sessionEmail = useSelector((state) => state?.users?.user?.email);
  const sessionRole = useSelector((state) => state?.users?.user?.role);

  const [userName, setUserName] = useState("");
  const [role, setRole] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [userNameUpdate, setUserNameUpdate] = useState("");
  const [roleUpdate, setRoleUpdate] = useState("");
  const [passwordUpdate, setPasswordUpdate] = useState("");
  const [confirmPasswordUpdate, setConfirmPasswordUpdate] = useState("");
  const [targetUser, setTargetUser] = useState({});
  const [roleSelect, setRoleSelect] = useState({ data: roleConstants, disabled: true });

  const [searchValue, setSearchValue] = useState("");
  const [sortUsers, setSortUsers] = useState([""]);

  const [error, setError] = useState("");

  const [editUser, setEditUser] = useState("emtpy");
  const [publicAPI, setPublicAPI] = useState("emtpy");

  const [isLoading, setIsLoading] = useState(true);

  const [range, setRange] = useState(100);
  const [tempRange, setTempRange] = useState(100);

  const [show, setShow] = useState(null);

  useEffect(() => {
    if (apiLimiter) {
      setRange(apiLimiter);
      setTempRange(apiLimiter);
      setIsLoading(false);
    }
  }, [apiLimiter]);

  useEffect(() => {
    dispatch(publicKeyCheckRequest(publicAPI));
    dispatch(apiLimitterGetRequest(publicAPI));
  }, [dispatch, publicAPI]);

  useEffect(() => {
    dispatch(getAdminRoleRequest());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getAllUsersRequest());
  }, [dispatch]);

  useEffect(() => {
    if (searchValue)
      setSortUsers(
        users.filter((user) => user.username.toLowerCase().includes(searchValue.toLowerCase())),
      );
    else setSortUsers([]);
  }, [searchValue, users]);

  useEffect(() => {
    if (userChanged) {
      dispatch(getAllUsersRequest());
      dispatch(setUserChanged(false));
    }
  }, [userChanged]);

  if (!users) {
    return <Backdrop show={!users} />;
  }

  // A user document only references its role, so resolve the name from the role
  // documents /api/users/role returns.
  const roleNameOf = (user) => {
    const roleId = user?.roles?.[0]?.["$oid"];
    const match = Array.isArray(findAdminRole)
      ? findAdminRole.find((roleItem) => roleItem?._id?.["$oid"] === roleId)
      : null;
    if (match?.name) return match.name;
    // The account we are logged in as is known from the session either way.
    if (sessionEmail && user?.email === sessionEmail && sessionRole)
      return sessionRole.toLowerCase();
    return "user";
  };

  const roleLabelOf = (user) => {
    const name = roleNameOf(user);
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  // Only a superadmin may assign a role, and never on a superadmin account.
  // Everyone else sees the account's current role read-only — including admins
  // looking at their own row, which must show "admin" rather than "user".
  const roleSelectionFor = (user) => {
    const targetRole = roleNameOf(user);

    if (sessionRole === "SUPERADMIN" && targetRole !== "superadmin") {
      return {
        data: supRoleConstants,
        disabled: false,
        selected: supRoleConstants.find((item) => item.value === targetRole) || supRoleConstants[0],
      };
    }

    const option = allRoleConstants.find((item) => item.value === targetRole) || roleConstants[0];
    return { data: [option], disabled: true, selected: option };
  };

  const editUserHandler = (user) => {
    if (editUser !== user.username) {
      const selection = roleSelectionFor(user);
      setEditUser(user.username);
      setPublicAPI(" ");
      setUserNameUpdate(user.username);
      setEmail(user.email);
      setTargetUser(user);
      setRoleSelect({ data: selection.data, disabled: selection.disabled });
      setRoleUpdate(selection.selected);
    } else {
      setEditUser(" ");
    }
  };

  const publicAPIHandler = (user) => {
    if (publicAPI !== user.username) {
      setPublicAPI(user.username);
      setEditUser(" ");
      setUserNameUpdate(user.username);
      setEmail(user.email);
      setTargetUser(user);
    } else {
      setPublicAPI(" ");
    }
  };

  const returnAddUserComponent = () => {
    setEditUser(" ");
    setPublicAPI(" ");
  };

  const createUser = async () => {
    if (email && userName && role && password && confirmPassword) {
      const emailRegex =
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      const usernameRegex = /^[a-z0-9]+$/;
      if (emailRegex.test(email)) {
        if (password === confirmPassword) {
          if (usernameRegex.test(userName)) {
            if (userName.length > 3 && userName.length < 16) {
              const strength = evaluatePassword(password, { username: userName, email });
              if (!strength.valid) {
                setError(strength.errors[0]);
                setShow(true);
                return;
              }
              const roleValue = role.value;
              const user = { username: userName, email, password };
              const userSpace = {
                userValue: user,
                role: roleValue,
              };
              dispatch(createUserRequest(userSpace));
              setError(null);
            } else {
              setError("Please enter a username between 3 and 15 characters long.");
              setShow(true);
            }
          } else {
            setError(
              "Please enter a valid username.Username can only contain lowercase letters and numbers.",
            );
            setShow(true);
          }
        } else {
          setError("Make sure your passwords are the same");
          setShow(true);
        }
      } else {
        setError("Please enter a valid email");
        setShow(true);
      }
    } else {
      setError("Please fill in all fields!");
      setShow(true);
    }
  };

  const updateUser = async () => {
    if (email && userNameUpdate && roleUpdate && passwordUpdate && confirmPasswordUpdate) {
      if (passwordUpdate !== confirmPasswordUpdate) {
        setError("Make sure your passwords are the same");
        setShow(true);
      } else {
        const strength = evaluatePassword(passwordUpdate, {
          username: userNameUpdate,
          email,
        });
        if (!strength.valid) {
          setError(strength.errors[0]);
          setShow(true);
          return;
        }
        const user = {
          id: targetUser?._id?.["$oid"],
          username: userNameUpdate,
          email: email,
          // targetUser holds the row as it was before this edit, so its email
          // still identifies the account against the session user.
          isProfile: Boolean(sessionEmail) && targetUser?.email === sessionEmail,
        };

        if (passwordUpdate) user.password = passwordUpdate;
        // Only sent when the role is actually editable; the backend rejects a
        // role change from anyone but a superadmin either way.
        if (!roleSelect.disabled && roleUpdate?.value) user.role = roleUpdate.value;
        dispatch(updateUserRequest(user));
      }
    } else {
      setError("Please fill in all fields!");
      setShow(true);
    }
  };

  const deleteUser = async (user) => {
    if (user._id.$oid === "62e0dcc5f1892e7b41284687") {
      setError("Initial user cannot be deleted!");
      setShow(true);
    } else {
      const userSpace = {
        id: user._id.$oid,
      };

      dispatch(deleteUserRequest(userSpace));
    }
  };

  const saveRange = (username) => {
    if (tempRange !== apiLimiter) {
      const data = { tempRange: tempRange, user: username };
      dispatch(apiLimitterSetRequest(data));
      setRange(tempRange);
    }
  };

  const createPublicApiKey = (username) => {
    dispatch(publicKeyRegRequest(username));
  };

  return (
    <div className="users-accounts">
      <div className="flex flex-align-items-center flex-justify-content-between">
        <h1>User Accounts</h1>
      </div>
      <div className="users-accounts__body">
        <div className="users-accounts__body-header">
          <div className="flex flex-align-items-center flex-justify-content-start flex-wrap">
            <h3>User accounts</h3>
            <p className="analysis-images__description">
              <span> NOTE:</span> When setting up for the first time, please update passwords for
              the built-in “admin” , “analyst”, “operator”, and “auditor” accounts These accounts
              cannot be removed. Add/remove users or reset account passwords below.
            </p>
          </div>
        </div>
        <div className={`users-accounts__create flex-1`}>
          <h3>Add New User</h3>
          <p className="label">Email (used as a username while logging in)</p>
          <TextField onChange={(e) => setEmail(e.target.value)} placeholder={"Type Email"} />
          <AddUser
            role={role}
            setRole={setRole}
            userName={userName}
            setPassword={setPassword}
            setConfirmPassword={setConfirmPassword}
            setUserName={setUserName}
            password={password}
            confirmPassword={confirmPassword}
            email={email}
            error={error}
          />
          <div className="m-t-20 m-b-20">
            <button onClick={() => createUser()} className="primary-button">
              Add User
            </button>
          </div>
        </div>
        <div className="users-accounts__update">
          <div className="p-l-20 m-r-20">
            <h3 className="settings-article">User List</h3>
            <div
              className={`${
                width > 1270 ? "flex flex-align-items-stretch flex-justify-content-between " : ""
              }`}>
              <div
                className="users-accounts__update-item"
                style={{ width: "100%" }}>
                <div className="users-accounts__user-list">
                  <TextField
                    onChange={(e) => setSearchValue(e.target.value)}
                    value={searchValue}
                    placeholder="Search User"
                  />
                </div>
                <div className="users-accounts__user">
                  <p className="users-accounts__user-name users-accounts__user-header">User Name</p>
                  <p className="users-accounts__user-email users-accounts__user-header">Email</p>
                  <p className="users-accounts__user-role users-accounts__user-header">Role</p>
                  <p className="users-accounts__user-actions users-accounts__user-header">
                    Actions
                  </p>
                </div>
                <div >
                  {searchValue &&
                    users.length &&
                    sortUsers.map((user, index) => (
                      <div key={index}>
                        <div className="users-accounts__user">
                          <p className="users-accounts__user-name">{user.username}</p>
                          <p className="users-accounts__user-email">{user.email}</p>
                          <p className="users-accounts__user-role">{roleLabelOf(user)}</p>
                          <div className="users-accounts__user-actions">
                            <button
                              type="button"
                              className="users-accounts__action-button"
                              onClick={() => editUserHandler(user)}>
                              <EditIcon /> Edit
                            </button>
                            <button
                              type="button"
                              className="users-accounts__action-button"
                              onClick={() => publicAPIHandler(user)}>
                              <APIIcon /> API
                            </button>
                            <button
                              type="button"
                              className="users-accounts__action-button"
                              onClick={() => deleteUser(user)}>
                              <TrashBucketIcon /> Delete
                            </button>
                          </div>
                        </div>
                        {editUser === user.username ? (
                          <div id={user.username} className={`users-accounts__update-user flex-1`}>
                            <h3>Update User</h3>
                            <p className="label">Email (used as a username while logging in)</p>
                            <div className="users-accounts__email">
                              <TextField
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder={"Type Email"}
                              />
                            </div>
                            <ChangeFields
                              role={roleUpdate}
                              setRole={setRoleUpdate}
                              userName={userNameUpdate}
                              setPassword={setPasswordUpdate}
                              setConfirmPassword={setConfirmPasswordUpdate}
                              setUserName={setUserNameUpdate}
                              password={passwordUpdate}
                              confirmPassword={confirmPasswordUpdate}
                              email={email}
                              button={updateUser}
                              onCancel={returnAddUserComponent}
                              roleData={roleSelect.data}
                              roleDisabled={roleSelect.disabled}
                            />
                          </div>
                        ) : null}
                        {publicAPI === user.username ? (
                          <div id={user.username} className={`users-accounts__update-user flex-1`}>
                            <div className="publicapi__body__content">
                              <div className="publicapi__body__content__row">
                                <div className="publicapi__body__content__row__apikey">
                                  <p className="publicapi__body__content__row__apikey__header">
                                    Public API Key
                                  </p>
                                  <div className="publicapi__body__content__row__apikey__labels">
                                    {user.publicApiKey || userPublicKey ? (
                                      user.publicApiKey || userPublicKey
                                    ) : (
                                      <button
                                        className="primary-button"
                                        onClick={() => createPublicApiKey(user.username)}>
                                        Create Key
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div>
                                <p className="light publicapi__body__content__row__range-container-header">
                                  Public API Limiter ( Per minutes )
                                </p>
                                {isLoading ? (
                                  <p>Loading...</p>
                                ) : (
                                  <div className="publicapi__body__content__row__range-container">
                                    <div className="publicapi__body__content__row__range-container__data">
                                      <div></div>
                                      <input
                                        type="number"
                                        value={tempRange}
                                        onChange={(e) => setTempRange(Number(e.target.value))}
                                        min="1"
                                        max="2000"
                                        disabled={isLoading}
                                      />
                                      <div></div>
                                    </div>
                                    <input
                                      type="range"
                                      max="2000"
                                      onChange={(e) => setTempRange(Number(e.target.value))}
                                      value={tempRange}
                                      disabled={isLoading}
                                    />
                                  </div>
                                )}
                                <button
                                  className="primary-button"
                                  style={{ marginTop: "20px" }}
                                  onClick={() => saveRange(user.username)}
                                  disabled={isLoading}>
                                  Save
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    ))}
                  {!searchValue &&
                    users.length &&
                    users.map((user, index) => (
                      <div key={index}>
                        <div className="users-accounts__user">
                          <p className="users-accounts__user-name">{user.username}</p>
                          <p className="users-accounts__user-email">{user.email}</p>
                          <p className="users-accounts__user-role">{roleLabelOf(user)}</p>
                          <div className="users-accounts__user-actions">
                            <button
                              type="button"
                              className="users-accounts__action-button"
                              onClick={() => editUserHandler(user)}>
                              <EditIcon /> Edit
                            </button>
                            <button
                              type="button"
                              className="users-accounts__action-button"
                              onClick={() => publicAPIHandler(user)}>
                              <APIIcon /> API
                            </button>
                            <button
                              type="button"
                              className="users-accounts__action-button"
                              onClick={() => deleteUser(user)}>
                              <TrashBucketIcon /> Delete
                            </button>
                          </div>
                        </div>
                        {editUser === user.username ? (
                          <div id={user.username} className={`users-accounts__update-user flex-1`}>
                            <h3>Update User</h3>
                            <p className="label">Email (used as a username while logging in)</p>
                            <div className="users-accounts__email">
                              <TextField
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder={"Type Email"}
                              />
                            </div>
                            <ChangeFields
                              role={roleUpdate}
                              setRole={setRoleUpdate}
                              userName={userNameUpdate}
                              setPassword={setPasswordUpdate}
                              setConfirmPassword={setConfirmPasswordUpdate}
                              setUserName={setUserNameUpdate}
                              password={passwordUpdate}
                              confirmPassword={confirmPasswordUpdate}
                              email={email}
                              button={updateUser}
                              onCancel={returnAddUserComponent}
                              roleData={roleSelect.data}
                              roleDisabled={roleSelect.disabled}
                            />
                          </div>
                        ) : null}
                        {publicAPI === user.username ? (
                          <div id={user.username} className={`users-accounts__update-user flex-1`}>
                            <div className="publicapi__body__content">
                              <div className="publicapi__body__content__row">
                                <div className="publicapi__body__content__row__apikey">
                                  <p className="publicapi__body__content__row__apikey__header">
                                    Public API Key
                                  </p>
                                  <div className="publicapi__body__content__row__apikey__labels">
                                    {user.publicApiKey || userPublicKey ? (
                                      user.publicApiKey || userPublicKey
                                    ) : (
                                      <button
                                        className="primary-button"
                                        onClick={() => createPublicApiKey(user.username)}>
                                        Create Key
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div>
                                <p className="light publicapi__body__content__row__range-container-header">
                                  Public API Limiter ( Per minutes )
                                </p>
                                {isLoading ? (
                                  <p>Loading...</p>
                                ) : (
                                  <div className="publicapi__body__content__row__range-container">
                                    <div className="publicapi__body__content__row__range-container__data">
                                      <div></div>
                                      <input
                                        type="number"
                                        value={tempRange}
                                        onChange={(e) => setTempRange(Number(e.target.value))}
                                        min="1"
                                        max="2000"
                                        disabled={isLoading}
                                      />
                                      <div></div>
                                    </div>
                                    <input
                                      type="range"
                                      max="2000"
                                      onChange={(e) => setTempRange(Number(e.target.value))}
                                      value={tempRange}
                                      disabled={isLoading}
                                    />
                                  </div>
                                )}
                                <button
                                  className="primary-button"
                                  style={{ marginTop: "20px" }}
                                  onClick={() => saveRange(user.username)}
                                  disabled={isLoading}>
                                  Save
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Warning
        onClickOk={() => setShow(false)}
        show={show}
        setShow={setShow}
        text={error}
        article="Warning"
        cancelDisable={true}
        textOK="Okay"
      />
    </div>
  );
};

export default UserAccounts;
